import { SEARCH_SUGGEST_MIN_QUERY_LENGTH } from '@lib/shared';
import { useDebounceFn } from '@vueuse/core';
import type { ComputedRef, Ref } from 'vue';

import {
    AssetType,
    getApiSearchSuggestQueryKey,
    type SearchSuggestAuctionItemDto,
    type SearchSuggestDirectSaleItemDto,
    SellingMethod,
    useApiSearchSuggest,
} from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';
import { type PlatformSellingMethod } from '~/types/sale.type';

const DEBOUNCE_MS = 250;

export type HeaderSearchFlatItem = { sellingMethod: PlatformSellingMethod; slug: string };

export type UseHeaderSearchOptions = {
    onClose?: () => void;
};

export type UseHeaderSearchReturn = {
    inputValue: Ref<string>;
    debouncedValue: Ref<string>;
    highlightIndex: Ref<number>;
    isFetching: Ref<boolean>;
    isOpen: ComputedRef<boolean>;
    auctions: ComputedRef<SearchSuggestAuctionItemDto[]>;
    directSales: ComputedRef<SearchSuggestDirectSaleItemDto[]>;
    auctionsTotal: ComputedRef<number>;
    directSalesTotal: ComputedRef<number>;
    hasResults: ComputedRef<boolean>;
    flatItems: ComputedRef<HeaderSearchFlatItem[]>;
    getDetailRoute: (sellingMethod: PlatformSellingMethod, slug: string) => ReturnType<ReturnType<typeof useLocaleRoute>>;
    getViewAllRoute: (sellingMethod: PlatformSellingMethod, q: string) => ReturnType<ReturnType<typeof useLocaleRoute>>;
    getResultHighlightClass: (idx: number) => string;
    onEnter: () => void;
    onArrowDown: () => void;
    onArrowUp: () => void;
    onEscape: () => void;
    onClear: () => void;
    onResultClick: () => void;
    closeDropdown: () => void;
};

export const useHeaderSearch = (options: UseHeaderSearchOptions = {}): UseHeaderSearchReturn => {
    const router = useRouter();
    const localeRoute = useLocaleRoute();
    const { formatSlpRoute } = useSlpRouteUtils();

    const inputValue = ref('');
    const debouncedValue = ref('');
    const highlightIndex = ref(-1);
    const isManuallyClosed = ref(false);

    const setDebounced = useDebounceFn((val: string) => {
        debouncedValue.value = val;
    }, DEBOUNCE_MS);

    watch(inputValue, val => {
        setDebounced(val);
        highlightIndex.value = -1;
        isManuallyClosed.value = false;
    });

    const enoughChars = computed(() => debouncedValue.value.trim().length >= SEARCH_SUGGEST_MIN_QUERY_LENGTH);

    const suggestParams = computed(() => ({ q: debouncedValue.value }));

    const { data, isFetching } = useApiSearchSuggest(suggestParams, {
        query: { enabled: enoughChars, queryKey: getApiSearchSuggestQueryKey(suggestParams) },
    });

    const auctions = computed(() => data.value?.auctions ?? []);
    const directSales = computed(() => data.value?.directSales ?? []);
    const auctionsTotal = computed(() => data.value?.auctionsTotal ?? 0);
    const directSalesTotal = computed(() => data.value?.directSalesTotal ?? 0);
    const hasResults = computed(() => auctions.value.length > 0 || directSales.value.length > 0);

    const flatItems = computed<HeaderSearchFlatItem[]>(() => [
        ...auctions.value.map((item): HeaderSearchFlatItem => ({ sellingMethod: SellingMethod.Auction, slug: item.slug })),
        ...directSales.value.map((item): HeaderSearchFlatItem => ({ sellingMethod: SellingMethod.DirectSale, slug: item.slug })),
    ]);

    const isOpen = computed(() => enoughChars.value && !isManuallyClosed.value);

    const closeDropdown = (): void => {
        isManuallyClosed.value = true;
        highlightIndex.value = -1;
        options.onClose?.();
    };

    const getDetailRoute: UseHeaderSearchReturn['getDetailRoute'] = (sellingMethod, slug) => {
        return localeRoute({
            name: sellingMethod === SellingMethod.Auction ? ROUTE_NAMES.AUCTION_DETAIL : ROUTE_NAMES.DIRECT_SALE_DETAIL,
            params: { slug },
        });
    };

    const getViewAllRoute: UseHeaderSearchReturn['getViewAllRoute'] = (sellingMethod, q) => {
        const target = formatSlpRoute({ sellingMethod, assetType: AssetType.Bus }, { q });

        return localeRoute({ name: target.name, params: target.params, query: target.query });
    };

    const onEnter = (): void => {
        const trimmed = inputValue.value.trim();

        if (trimmed.length < SEARCH_SUGGEST_MIN_QUERY_LENGTH) return;

        // Only navigate to a detail page when the user has explicitly highlighted
        // an item via arrow keys. Otherwise, Enter always goes to the Buy Now
        // listing with the query applied (Google-style "press Enter to search").
        const highlighted = flatItems.value[highlightIndex.value];

        if (highlighted) {
            const route = getDetailRoute(highlighted.sellingMethod, highlighted.slug);

            if (route) router.push(route);
            closeDropdown();

            return;
        }

        const route = getViewAllRoute(SellingMethod.DirectSale, trimmed);

        if (route) router.push(route);
        closeDropdown();
    };

    const onArrowDown = (): void => {
        if (flatItems.value.length === 0) return;
        highlightIndex.value = Math.min(highlightIndex.value + 1, flatItems.value.length - 1);
    };

    const onArrowUp = (): void => {
        if (flatItems.value.length === 0) return;
        highlightIndex.value = Math.max(highlightIndex.value - 1, -1);
    };

    const onEscape = (): void => {
        closeDropdown();
    };

    const onClear = (): void => {
        inputValue.value = '';
        debouncedValue.value = '';
        highlightIndex.value = -1;
    };

    const onResultClick = (): void => {
        closeDropdown();
    };

    const getResultHighlightClass = (idx: number): string => (highlightIndex.value === idx ? 'bg-neutral-100' : '');

    return {
        inputValue,
        debouncedValue,
        highlightIndex,
        isFetching,
        isOpen,
        auctions,
        directSales,
        auctionsTotal,
        directSalesTotal,
        hasResults,
        flatItems,
        getDetailRoute,
        getViewAllRoute,
        getResultHighlightClass,
        onEnter,
        onArrowDown,
        onArrowUp,
        onEscape,
        onClear,
        onResultClick,
        closeDropdown,
    };
};
